const delay = (ms) => new Promise((r) => setTimeout(r, ms));

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function normalizeRelatedProduct(item = {}) {
  const id = Number(item?.id ?? item?.ID ?? item?.post?.ID ?? item?.post?.id);

  return {
    id,
    title:
      item?.title?.rendered ??
      item?.title ??
      item?.post_title ??
      item?.post?.title?.rendered ??
      '제목 없음',
    thumbnail:
      item?.thumbnail ??
      item?.featured_image ??
      item?._embedded?.['wp:featuredmedia']?.[0]?.source_url ??
      '',
  };
}

function renderRelatedProducts(items = [], { hidePrice = false } = {}) {
  const products = items.map(normalizeRelatedProduct).filter((item) => item.id);

  if (!products.length) return '';

  // 가이드 관련 상품은 원본 dc_product 금액이 매장 판매가와 달라 혼란을 주므로
  // 모달에서 금액을 숨긴다 (overrides.price가 빈 값이면 금액 영역 미렌더링)
  const overrides = hidePrice ? `, overrides: { price: '' }` : '';

  const cards = products
    .map((item) => {
      const title = escapeHtml(item.title);
      const thumbnail = item.thumbnail
        ? `<img class="sheet-related-product__image" src="${escapeHtml(item.thumbnail)}" alt="${title}" loading="lazy" />`
        : `<span class="sheet-related-product__placeholder">No Image</span>`;

      return `
        <li class="sheet-related-product">
          <button
            type="button"
            class="sheet-related-product__button"
            onclick="window.Alpine?.store('modal')?.showPost({ id: ${item.id}, postType: 'dc_product', size: 'sm'${overrides} })"
          >
            <span class="sheet-related-product__thumb">${thumbnail}</span>
            <span class="sheet-related-product__title">${title}</span>
          </button>
        </li>
      `;
    })
    .join('');

  return `
    <section class="sheet-related-products" aria-label="관련 상품">
      <hr class="sheet-related-products__divider" />
      <h3 class="sheet-related-products__heading">관련 상품</h3>
      <ul class="sheet-related-products__grid">${cards}</ul>
    </section>
  `;
}

async function fetchJSON(url) {
  const res = await fetch(url, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Request failed: ${res.status} ${res.statusText}\n${text}`);
  }

  return res.json();
}
export function createSheetStore({
  endpointBase = '/wp-json/dcraft/v1',
  closeOnBackdrop = true,
  closeOnEsc = true,
  fetchJSON, // ✅ 외부에서 주입
} = {}) {
  return {
    // state
    open: false,
    title: '',
    content: '',
    loading: false,
    type: 'default',
    size: 'lg',
    targetPost: '',

    closeOnBackdrop,
    closeOnEsc,

    _scrollTop: 0,

    // handlers
    close() {
      this.resetScroll();
      this.open = false;
      this.loading = false;
      setTimeout(() => {
        document.querySelector('.sheet-root')?.classList.remove('sheet-open');
        document.documentElement.removeAttribute('data-overlay-open');
      }, 100);
    },

    onBackdrop() {
      if (this.closeOnBackdrop) this.close();
    },

    onKeydown(e) {
      if (!this.open) return;
      if (!this.closeOnEsc) return;
      if (e.key === 'Escape') this.close();
    },

    onScroll(e) {
      this._scrollTop = e?.target?.scrollTop ?? 0;
    },

    resetScroll() {
      this._scrollTop = 0;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const sheetBody = document.querySelector('.sheet-body');

          if (sheetBody) {
            sheetBody.style.scrollBehavior = 'auto';
            sheetBody.scrollTop = 0;

            requestAnimationFrame(() => {
              sheetBody.style.scrollBehavior = '';
            });
          }
        });
      });
    },

    openContent({
      title = '',
      content = '',
      size = 'lg',
      type = 'default',
    } = {}) {
      this.title = title;
      this.content =
        content || '<div class="text-center">표시할 내용이 없습니다</div>';
      this.size = size;
      this.type = type;
      this.loading = false;
      this.open = true;
      this.resetScroll();

      setTimeout(() => {
        document.querySelector('.sheet-root')?.classList.add('sheet-open');
        document.documentElement.setAttribute('data-overlay-open', '');
      }, 50);
    },

    // ✅ 핵심: openById가 "열기 + 로딩 + fetch + 매핑"까지 책임
    async openById(id, target = 'guide') {
      const postId = Number(id);
      if (!postId) return;

      this.targetPost = target;

      // 1) UI 먼저 오픈
      this.open = true;
      this.loading = true;
      this.title = '';
      this.content = '';
      this.type = 'default';
      this.resetScroll();

      setTimeout(() => {
        document.querySelector('.sheet-root')?.classList.add('sheet-open');
        document.documentElement.setAttribute('data-overlay-open', '');
      }, 50);

      try {
        if (typeof fetchJSON !== 'function') {
          throw new Error('createSheetStore: fetchJSON is not provided');
        }

        // 2) 데이터 가져오기 (너의 실제 라우트에 맞게 수정)
        // 예: /wp-json/dcraft/v1/${target}/175
        // const data = await fetchJSON(`${endpointBase}/${target}/${postId}`);
        const [data] = await Promise.all([
          fetchJSON(`${endpointBase}/${target}/${postId}`),
          delay(10), // ← 여기서 체감 조절 (300~600ms 권장)
        ]);

        // 3) 매핑 (서버가 어떤 키로 주는지에 맞춰 확실히 커버)
        const title =
          data?.title?.rendered ??
          data?.title ??
          data?.post?.title?.rendered ??
          '';

        let html =
          data?.content?.rendered ??
          data?.content ??
          data?.html ??
          data?.post?.content?.rendered ??
          '';

        html += renderRelatedProducts(
          data?.related_products ?? data?.acf?.related ?? [],
          { hidePrice: target === 'guide' }
        );

        if (data?.acf?.product) {
          html += `<hr class="my-10" /><h3 class="font-semibold text-xl">판매 상품</h3>`;
          const product = data.acf.product;
          if (product && Array.isArray(product) && product.length > 0) {
            html += '<ul class="list-none m-0 p-0 flex flex-wrap gap-2">';
            product.forEach((item) => {
              const itemTitle = item?.title || '제목 없음';
              const itemLink = item?.url || '#';
              html += `<li class="p-0 m-0">
              <a
                href="${itemLink}"
                target="_blank"
                rel="noopener"
                class="btn--outline">${itemTitle}</a></li>`;
            });
            html += '</ul>';
          }
        }

        this.title = title;
        this.content =
          html || '<div class="text-center">표시할 내용이 없습니다</div>';
      } catch (e) {
        console.error(e);
        this.title = '오류';
        this.content = '<p>콘텐츠를 불러오지 못했습니다.</p>';
        this.type = 'error';
      } finally {
        this.loading = false;
        this.resetScroll();
      }
    },
  };
}
